import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Input.module.css';

import { useForm, useFormContext } from 'react-hook-form';

import { useUpdateNoteMutation } from '../../services/library';
import { useUpdateSharedNoteMutation } from '../../services/share';
import { useUpdateResourceNoteMutation } from '../../services/drive';

import { useAppSelector } from '../../hooks/useRedux';
import { useSlice } from '../../hooks/useSlice';

import { selectMenu } from '../../features/infoSlice';

import {
  COPYRIGHT_PROTECTION_NOTICE,
  ENTER_PLACEHOLDER,
  PROFANITY_WARNING_MESSAGE,
} from '../../constants';

import {
  convertingSupportList,
  getOnlyNumber,
  limitMaxLen,
} from '../../utils/util';
import useUpload from '../../hooks/useUpload';

import { useValidateProfanityMutation } from '../../services/common';
import useModal from '../../hooks/useModal';
import { selectModalById } from '../../features/modalSlice';
import { getCookie } from '../../utils/cookie';

interface Input {
  type: string;
  id: string;
  classnames?: string[];
  value?: string | number;
  placeholder?: string;
  isHidden?: boolean;
  isDisabled?: boolean;
  checked?: boolean;
  children?: React.ReactElement;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement>,
    isFilled: boolean
  ) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  limit?: number;
  onlyNumber?: boolean;
  accept?: string;
  subKey?: string;
  onValidate?: (isValidate: any) => void;
}

export default function Input({
  type,
  id,
  classnames,
  value,
  placeholder,
  isHidden,
  isDisabled,
  checked,
  children,
  onChange,
  onBlur,
  limit,
  onlyNumber = false,
  accept,
  subKey,
  onValidate,
}: Input) {
  const cx = classNames.bind(styles);
  const { selectSelectedId } = useSlice();
  const menu = useAppSelector(selectMenu);

  const methods = useFormContext() || useForm();
  const {
    register,
    setValue,
    getValues,
    setError,
    clearErrors,
    setFocus,
    formState: { errors },
  } = methods;

  const projectId = useAppSelector(selectSelectedId);
  const copyrightModal = useAppSelector((state) =>
    selectModalById(state, 'uploadCopyright')
  );
  const [isCopyrightModalOpened, setIsCopyrightModalOpened] = useState(false);
  const labelRef = useRef<HTMLLabelElement>(null);

  const [updateNote] = useUpdateNoteMutation();
  const [updateSharedNote] = useUpdateSharedNoteMutation();
  const [updateResourceNote] = useUpdateResourceNoteMutation();
  const [validateProfanity] = useValidateProfanityMutation();

  const { upload } = useUpload();
  const { toggleModal } = useModal();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      const valueArr = Object.values(getValues());
      const isFilled = !valueArr.some(
        (val) => !val || (typeof val === 'string' && val.trim() === '')
      );
      onChange(e, isFilled);
    } else {
      onChangeHandlers[type]?.(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlurHandlers[id]?.(e);
  };

  const uploadFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files }: any = e.target;
    upload(e, id, files);
  };

  const onChangeText = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    if (limit) {
      target.value = limitMaxLen(value, limit);
    }
    if (onlyNumber) {
      target.value = getOnlyNumber(target.value);
    }
  };

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    setValue(id, +checked);
  };

  const onChangeHandlers: {
    [key: string]: (e: React.ChangeEvent<HTMLInputElement>) => void;
  } = {
    file: uploadFiles,
    text: onChangeText,
    number: onChangeText,
    checkbox: handleCheck,
  };

  const validate = async (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    try {
      const res = await validateProfanity(value);
      if ('data' in res) {
        onValidate && onValidate(res.data);
        if (!res.data) {
          setError(id, {
            type: 'filter',
            message: PROFANITY_WARNING_MESSAGE,
          });
          setFocus(id);
          return;
        } else {
          clearErrors(id);
        }
      }
    } catch (error) {
      console.error('Fail to validate profanity', error);
    }
  };

  const onBlurHandlers: {
    [key: string]: (e: React.FocusEvent<HTMLInputElement>) => void;
  } = {
    // CHECK: submit을 호출할 방법은 없나
    copyrightOwner: validate,
    copyrightSource: validate,
    projectNote: async () => {
      const updateNotebyMenu: { [key: string]: any } = {
        library: updateNote,
        share: updateSharedNote,
      };
      try {
        await updateNotebyMenu[menu]({
          projectId,
          data: methods.getValues('projectNote'),
        }).unwrap();
      } catch (error) {
        console.error('Failed to update ProjectNote', error);
      }
    },
    note: async () => {
      try {
        await updateResourceNote({
          resourceId: projectId,
          data: methods.getValues('note'),
        }).unwrap();
      } catch (error) {
        console.error('Failed to update ProjectNote', error);
      }
    },
    title: validate,
    publisher: validate,
    author: validate,
    ...(subKey !== 'web' && { data: validate }),
    publicKeyword1: validate,
    publicKeyword2: validate,
    publicKeyword3: validate,
  };

  const handleBeforeInput = (e: React.MouseEvent<HTMLInputElement>) => {
    if (
      id.includes('library') &&
      type === 'file' &&
      !isCopyrightModalOpened &&
      !getCookie('selpubAlertCopyrightPopup')
    ) {
      e.preventDefault();
      toggleModal({
        id: 'uploadCopyright',
        type: 'alert',
        contentTitle: COPYRIGHT_PROTECTION_NOTICE,
      });
      setIsCopyrightModalOpened(true);
    }
  };

  useEffect(() => {
    if (isCopyrightModalOpened && !copyrightModal?.isOpen) {
      labelRef.current?.click();
      setIsCopyrightModalOpened(false);
    }
  }, [copyrightModal, isCopyrightModalOpened]);

  useEffect(() => {
    setValue(
      id,
      value && typeof value === 'string' && limit
        ? limitMaxLen(value, limit)
        : value
    );
  }, [value]);

  return (
    <>
      <label htmlFor={id} className={cx('hide')} ref={labelRef}></label>
      <input
        type={type}
        id={id}
        {...register(`${id}` as const, {
          onChange: handleChange,
          onBlur: onBlur ?? handleBlur,
        })}
        defaultValue={value}
        className={cx(classnames)}
        hidden={type === 'file' || isHidden}
        placeholder={placeholder || ENTER_PLACEHOLDER}
        disabled={isDisabled}
        checked={checked}
        accept={
          accept ??
          (menu === 'library' || id.includes('library')
            ? convertingSupportList.map((ext) => `.${ext}`).join()
            : '')
        }
        multiple={type === 'file' && !id.includes('library')}
        onClick={handleBeforeInput}
      />
      {errors[id] && (
        <span className={cx('msg')}>{errors[id]?.message as string}</span>
      )}
      {children}
    </>
  );
}
